/**
 * Shared rule for matching a filled keg to an order/transfer/invoice line.
 * Server mirror: server/services/keg-line-match.js — keep behavior in sync.
 *
 * 1. Prefer productId equality when both sides have an id.
 * 2. Otherwise tolerant name: keg product name ⊆ line itemName, OR
 *    line product name ⊆ keg product name.
 * 3. Always require package size to match when both sizes are known.
 */

export function extractKegSizeKey(raw: string | null | undefined): string | null {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!s) return null;
  const bbl = s.match(/\b(1\/[246])\s*bbl\b/);
  if (bbl) return `${bbl[1]} bbl`;
  if (/\bfirkin\b/.test(s)) return 'firkin';
  if (/\bpin\b/.test(s)) return 'pin';
  return null;
}

export function isKegPackage(line: {
  packageType?: string | null;
  itemName?: string | null;
}): boolean {
  const s = String(line?.packageType || line?.itemName || '');
  return /keg/i.test(s) && !/shell/i.test(s);
}

/** Parse "Product (Keg - 1/4 bbl)" → productName + packageType when not carried. */
export function parseParenItem(itemName: string | null | undefined): {
  productName: string | null;
  packageType: string | null;
} {
  const m = String(itemName || '')
    .trim()
    .match(/^(.*)\s+\(([^()]+)\)\s*$/);
  if (!m) return { productName: null, packageType: null };
  return { productName: m[1].trim() || null, packageType: m[2].trim() || null };
}

export interface KegMatchSide {
  code?: string | null;
  productId?: number | null;
  productName?: string | null;
  packagingType?: string | null;
  packageType?: string | null;
}

export interface LineMatchSide {
  itemName?: string | null;
  productId?: number | null;
  productName?: string | null;
  packageType?: string | null;
}

export function lineProductName(line: LineMatchSide): string | null {
  const explicit = String(line?.productName || '').trim();
  if (explicit) return explicit;
  return parseParenItem(line?.itemName).productName;
}

export function linePackageType(line: LineMatchSide): string | null {
  const explicit = String(line?.packageType || '').trim();
  if (explicit) return explicit;
  return parseParenItem(line?.itemName).packageType;
}

function kegPackageRaw(keg: KegMatchSide): string {
  return String(keg?.packagingType || keg?.packageType || '').trim();
}

export function packageSizesEqual(keg: KegMatchSide, line: LineMatchSide): boolean {
  const kegSize = extractKegSizeKey(kegPackageRaw(keg));
  const lineSize = extractKegSizeKey(linePackageType(line) || line?.itemName || '');
  if (!kegSize || !lineSize) return true;
  return kegSize === lineSize;
}

export function packageSizeMismatch(
  keg: KegMatchSide,
  line: LineMatchSide
): { kegSize: string; lineSize: string } | null {
  const kegSize = extractKegSizeKey(kegPackageRaw(keg));
  const lineSize = extractKegSizeKey(linePackageType(line) || line?.itemName || '');
  if (!kegSize || !lineSize) return null;
  if (kegSize === lineSize) return null;
  return { kegSize, lineSize };
}

function hasProductId(value: number | null | undefined): boolean {
  if (value == null || (value as unknown) === '') return false;
  const n = Number(value);
  return Number.isFinite(n);
}

/** Product-only match (ignores size). */
export function productMatchesLine(keg: KegMatchSide, line: LineMatchSide): boolean {
  if (!isKegPackage(line)) return false;

  if (hasProductId(line?.productId) && hasProductId(keg?.productId)) {
    return Number(line.productId) === Number(keg.productId);
  }

  const kegName = String(keg?.productName || '')
    .trim()
    .toLowerCase();
  if (!kegName) return false;

  const item = String(line?.itemName || '')
    .trim()
    .toLowerCase();
  if (item && item.includes(kegName)) return true;

  const lineProd = String(lineProductName(line) || '')
    .trim()
    .toLowerCase();
  if (lineProd && kegName.includes(lineProd)) return true;

  return false;
}

export function kegMatchesLine(keg: KegMatchSide, line: LineMatchSide): boolean {
  return productMatchesLine(keg, line) && packageSizesEqual(keg, line);
}

export type KegLineMatchResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'PRODUCT' | 'SIZE' | 'NOT_KEG';
      code: string;
      message: string;
      kegSize?: string;
      lineSize?: string;
    };

export function matchKegToLine(
  keg: KegMatchSide,
  line: LineMatchSide,
  opts: { kegCode?: string } = {}
): KegLineMatchResult {
  const code =
    String(opts.kegCode || keg?.code || '')
      .trim()
      .toUpperCase() || 'keg';

  if (!isKegPackage(line)) {
    return {
      ok: false,
      reason: 'NOT_KEG',
      code: 'KEG_NOT_KEG_LINE',
      message: `${line?.itemName || 'Line'} is not a keg package`,
    };
  }

  const sizeMis = packageSizeMismatch(keg, line);
  const productOk = productMatchesLine(keg, line);

  if (productOk && sizeMis) {
    return {
      ok: false,
      reason: 'SIZE',
      code: 'KEG_WRONG_SIZE',
      message: `Keg ${code} is ${sizeMis.kegSize}; this line is ${sizeMis.lineSize}`,
      kegSize: sizeMis.kegSize,
      lineSize: sizeMis.lineSize,
    };
  }

  if (!productOk) {
    const label = keg?.productName || code;
    return {
      ok: false,
      reason: 'PRODUCT',
      code: 'KEG_WRONG_PRODUCT',
      message: `Keg ${code} is not on this order (${label})`,
    };
  }

  return { ok: true };
}
